import { useQuery } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { FC, useState } from 'react';
import styled from 'styled-components';

import AddButton from '@/components/AddButton';
import Button from '@/components/Button';
import { PageHeading, SrOnly } from '@/components/Layout/Parts';
import SearchBar from '@/components/SearchBar';
import {
  FullWidthRow,
  ResponsiveTable,
  Table,
  TableContent,
  Th,
} from '@/components/Table';
import useDebounce from '@/hooks/useDebounce';
import { UserResourceV2 } from '@/types/resources';
import { api } from '@/utils/api';

import UserModal from './components/UserModal';
import UserTableRow from './components/UserTableRow';

const StyledTable = styled(Table)`
  th,
  td {
    &:first-child {
      padding-left: 24px;
    }
  }
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
`;

const Users: FC = () => {
  const [showUserModal, setShowUserModal] = useState(false);

  const [search, setSearch] = useState('');

  const debouncedSearch = useDebounce(search, 500);

  const {
    data: users,
    isLoading,
    isError,
  } = useQuery(
    [
      'users_v2',
      {
        ...(debouncedSearch.length > 2 && {
          search: debouncedSearch,
        }),
      },
    ],
    () =>
      api.get<never, UserResourceV2[]>('/v2/users', {
        params: {
          ...(debouncedSearch.length > 2 && {
            'filter[search]': debouncedSearch,
          }),
        },
      }),
  );

  return (
    <>
      <Header>
        <PageHeading>Users Management</PageHeading>
        <Button onClick={() => setShowUserModal(true)}>Add User</Button>
      </Header>
      <section>
        <SearchBar
          value={search}
          setValue={setSearch}
          placeholder="Search by name or email"
        />
        <ResponsiveTable>
          <StyledTable>
            <thead>
              <tr>
                <Th>Name</Th>
                <Th>Email</Th>
                <Th>Role</Th>
                <Th>Teams</Th>
                <Th>Companies</Th>
                <Th>Departments</Th>
                <Th $actionsCol>
                  <SrOnly>Actions</SrOnly>
                </Th>
              </tr>
            </thead>
            <tbody>
              <TableContent
                colSpan={7}
                isLoading={isLoading}
                hasData={!!users?.length}
                isError={isError}
              >
                {users?.map((user) => (
                  <UserTableRow key={user.id} user={user} />
                ))}
              </TableContent>
              <FullWidthRow colSpan={7}>
                <AddButton onClick={() => setShowUserModal(true)}>
                  Add new user
                </AddButton>
              </FullWidthRow>
            </tbody>
          </StyledTable>
        </ResponsiveTable>
        <AnimatePresence>
          {showUserModal && (
            <UserModal onClose={() => setShowUserModal(false)} />
          )}
        </AnimatePresence>
      </section>
    </>
  );
};

export default Users;
